import { useLayoutEffect, useState } from "react";

import { isDevEnv } from "@excalidraw/common";

import { STORAGE_KEYS } from "./app_constants";

export type DevicePreviewMode = "desktop" | "tablet" | "phone";

const DEVICE_PREVIEW_WIDTHS: Record<DevicePreviewMode, number | null> = {
  desktop: null,
  tablet: 834,
  phone: 390,
};

const isDevicePreviewMode = (value: unknown): value is DevicePreviewMode => {
  return value === "desktop" || value === "tablet" || value === "phone";
};

const getStoredDevicePreviewMode = (): DevicePreviewMode => {
  if (!isDevEnv() || typeof window === "undefined") {
    return "desktop";
  }

  try {
    const storedMode = window.localStorage.getItem(
      STORAGE_KEYS.LOCAL_STORAGE_DEVICE_PREVIEW,
    );
    if (isDevicePreviewMode(storedMode)) {
      return storedMode;
    }
  } catch {
    // ignore storage access issues (for example private mode restrictions)
  }

  return "desktop";
};

export const useHandleDevicePreview = () => {
  const [devicePreviewMode, setDevicePreviewModeState] =
    useState<DevicePreviewMode>(getStoredDevicePreviewMode);

  const setDevicePreviewMode = (mode: DevicePreviewMode) => {
    if (!isDevEnv()) {
      return;
    }

    setDevicePreviewModeState(mode);
  };

  useLayoutEffect(() => {
    if (!isDevEnv() || typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        STORAGE_KEYS.LOCAL_STORAGE_DEVICE_PREVIEW,
        devicePreviewMode,
      );
    } catch {
      // ignore storage access issues (for example private mode restrictions)
    }
  }, [devicePreviewMode]);

  return {
    devicePreviewMode,
    devicePreviewWidth: DEVICE_PREVIEW_WIDTHS[devicePreviewMode],
    setDevicePreviewMode,
  } as const;
};
